import { useMemo, useCallback } from 'react';
import { useRoomPlayers } from './useRoomPlayers';
import { useMedia } from './useMedia';

// Pentatonic scale intervals: C, D, E, G, A (in semitones)
const PENTATONIC_PATTERN = [0, 2, 4, 7, 9];

// Use C5 as the base (0 offset) - the synthService's natural base note
const BASE_NOTE_OFFSET = 0;

export function useMediaSynth() {
  const { players } = useRoomPlayers();
  const { playNote } = useMedia();

  // Create memoized map of playerId -> semitone offset
  const playerNoteMap = useMemo(() => {
    const map = new Map<string, number>();

    if (players.length === 0) {
      return map;
    }

    // Calculate how many octaves we need to span
    // Each octave has 5 notes (pentatonic), so we need Math.ceil(playerCount / 5) octaves
    const octavesNeeded = Math.ceil(players.length / 5);

    // Calculate total range in semitones (centered around C5 - the synth's base note)
    // We'll distribute players evenly around the base note
    const totalRange = octavesNeeded * 12; // Full octaves in semitones
    const halfRange = totalRange / 2;

    // Start from the lowest note (below C5)
    const startOffset = BASE_NOTE_OFFSET - halfRange;

    // Map each player to a note
    players.forEach((player, index) => {
      // Calculate which octave and which note in the pentatonic scale
      const octaveIndex = Math.floor(index / 5);
      const noteIndex = index % 5;

      // Calculate semitone offset from start
      const octaveOffset = octaveIndex * 12;
      const noteOffset = PENTATONIC_PATTERN[noteIndex];
      const totalOffset = startOffset + octaveOffset + noteOffset;

      map.set(player.playerId, totalOffset);
    });

    return map;
  }, [players]);

  // Function to play note for a specific player
  const playNoteForPlayer = useCallback(
    (playerId: string) => {
      const semitones = playerNoteMap.get(playerId);
      if (semitones !== undefined) {
        playNote(semitones);
      }
    },
    [playerNoteMap, playNote]
  );

  return { playNoteForPlayer };
}
